// RapidGradeScreen.js
// Final grade display for a completed Rapid CCS case.
// React 16.14 compatible — class component.
//
// Props (all data flows down from RapidCcsApp):
//   grade          grade result object from api.gradeRapidCcs   (required)
//                  grade.GradeFeedback.SUMMARY, when present, is shown as a
//                  short "Case Summary" card above the category breakdown.
//   diagnosis      correct diagnosis string                     (required)
//   onBackToCases  () => void                                   (required)
//   onRetry        () => void — retry this case                 (optional)
//   PreventiveCareGrading  boolean — when false, preventive care
//                  items are shown in blue with a "not graded" note (optional)

import React from "react";

// Maps GradeColor ("Correct - Green", "Optional - Blue", "Incorrect - Red")
// to a tone used as a CSS modifier. Unknown/missing colors fall back to red.
function gradeTone(item) {
  const c = (item.GradeColor || "").toLowerCase();
  if (c.indexOf("green") !== -1) return "green";
  if (c.indexOf("blue") !== -1) return "blue";
  if (c.indexOf("gray") !== -1 || c.indexOf("grey") !== -1) return "gray";
  return "red";
}

function num(v, digits) {
  if (typeof v !== "number") return "—";
  return digits != null ? v.toFixed(digits) : String(v);
}

// Fixed display order for grade-category groups: Diagnosis, Treatment,
// Preventive Care, then Appropriate. Anything unrecognized falls to the end,
// in its original first-seen order.
function categoryRank(cat) {
  const c = (cat || "").toLowerCase();
  if (c.indexOf("diagnos") !== -1) return 0;
  if (c.indexOf("treat") !== -1) return 1;
  if (c.indexOf("prevent") !== -1) return 2;
  if (c.indexOf("appropriate") !== -1) return 3;
  return 4;
}

// Splits a leading "[Tag] rest of text" prefix off a sequence-log entry, if present.
function splitLogTag(entry) {
  const m = /^\[([^\]]+)\]\s*(.*)$/.exec(entry);
  if (m) return { tag: m[1], text: m[2] };
  return { tag: null, text: entry };
}

class RapidGradeScreen extends React.Component {
  componentDidMount() {
    // --- Copy protection ---
    document.addEventListener("contextmenu", this.disableContextMenu);
    document.addEventListener("keydown", this.disableCopyCut);
    document.addEventListener("copy", this.disableCopyEvent);
  }

  componentWillUnmount() {
    // --- Copy protection ---
    document.removeEventListener("contextmenu", this.disableContextMenu);
    document.removeEventListener("keydown", this.disableCopyCut);
    document.removeEventListener("copy", this.disableCopyEvent);
  }

  // --- Copy protection ---
  disableContextMenu = (event) => event.preventDefault();

  disableCopyCut = (event) => {
    if (
      (event.ctrlKey || event.metaKey) &&
      (event.key === "c" || event.key === "x")
    ) {
      event.preventDefault();
    }
  };

  disableCopyEvent = (event) => {
    event.preventDefault();
  };

  render() {
    const g = this.props.grade || {};
    const items = Array.isArray(g.GradeOut) ? g.GradeOut : [];
    const diagnosis = this.props.diagnosis;
    const preventiveOff = this.props.PreventiveCareGrading === false;
    console.log(this.props.grade);
    // Group by GradeCategory, preserving first-seen order.
    const order = [];
    const groups = {};
    items.forEach(function (it) {
      const cat = it.GradeCategory || "Other";
      if (!groups[cat]) {
        groups[cat] = [];
        order.push(cat);
      }
      groups[cat].push(it);
    });
    order.sort(function (a, b) {
      return categoryRank(a) - categoryRank(b);
    });

    // Each grading category pairs an achieved score with its max weight so the
    // new score cards can show a fraction, a progress bar, and a percent badge.
    const categories = [
      {
        label: "Diagnosis",
        score: g.FinalDiagnosisGrade,
        weight: g.AdjustedWeightDiagnosis,
      },
      {
        label: "Treatment",
        score: g.FinalTreatmentGrade,
        weight: g.AdjustedWeightTreatment,
      },
      {
        label: "Preventative",
        score: g.FinalPreventiveGrade,
        weight: g.AdjustedWeightPreventative,
      },
      {
        label: "Appropriate",
        score: g.FinalAppropriateGrade,
        weight: g.AdjustedWeightAppropriate,
      },
    ].filter(function (c) {
      return typeof c.weight === "number";
    });

    // c.score arrives as a percentage (0-100), not a point value.
    // Points earned = percentage of that category's max weight.
    function catPoints(score, weight) {
      if (typeof score !== "number" || typeof weight !== "number") {
        return null;
      }
      return (score / 100) * weight;
    }

    // % filled for a category's progress bar (clamped 0-100).
    // score is already a percentage, so just clamp it directly.
    function catPct(score) {
      if (typeof score !== "number") {
        return 0;
      }
      return Math.max(0, Math.min(100, score));
    }

    const totalWeight = categories.reduce(function (sum, c) {
      return sum + c.weight;
    }, 0);

    const yourPct =
      totalWeight > 0 && typeof g.TotalGrade === "number"
        ? (g.TotalGrade / totalWeight) * 100
        : null;
    const avgPct =
      totalWeight > 0 && typeof g.AverageFirstAttempt === "number"
        ? (g.AverageFirstAttempt / totalWeight) * 100
        : null;

    return (
      <div className="rccs">
        <div className="rccs-grade-wrap">
          <div className="rcss-grade-wrap-inner">
            <div className="rccs-grade-hero">
              <div className="rccs-grade-hero__left">
                <span className="rccs-grade-hero__chip">Case grade</span>
                <div className="rccs-grade-hero__diag-name">
                  {diagnosis || "—"}
                </div>
              </div>

              <div className="rccs-grade-hero__right">
                <div className="rccs-grade-hero__score-col">
                  <div className="rccs-grade-hero__score-label">Your score</div>
                  <div className="rccs-grade-hero__score-big rccs-grade-hero__score-big--you">
                    {yourPct != null ? num(yourPct, 2) + "%" : "—"}
                  </div>
                  <div className="rccs-grade-hero__score-sub rccs-grade-hero__score-sub--you">
                    {num(g.TotalGrade, 2)} / {totalWeight} pts
                  </div>
                </div>
                <div className="rccs-grade-hero__vs">
                  <div className="rccs-grade-hero__vs-line" />
                  <div className="rccs-grade-hero__vs-label">vs</div>
                  <div className="rccs-grade-hero__vs-line" />
                </div>
                <div className="rccs-grade-hero__score-col">
                  <div className="rccs-grade-hero__score-label">
                    All users first-attempt avg
                  </div>
                  <div className="rccs-grade-hero__score-big rccs-grade-hero__score-big--avg">
                    {avgPct != null ? num(avgPct, 2) + "%" : "—"}
                  </div>
                  <div className="rccs-grade-hero__score-sub rccs-grade-hero__score-sub--avg">
                    {num(g.AverageFirstAttempt, 2)} / {totalWeight} pts
                  </div>
                </div>
              </div>
            </div>

            {categories.length > 0 && (
              <div className="rccs-weights">
                {categories.map(function (c) {
                  const p = catPct(c.score);
                  const pts = catPoints(c.score, c.weight);
                  if (
                    ((c.score === 100 && c.weight === 0) || preventiveOff) &&
                    c.label === "Preventative"
                  )
                    return <></>;
                  return (
                    <div className="rccs-weights__card" key={c.label}>
                      <div className="rccs-weights__cat-label">{c.label}</div>
                      <div className="rccs-weights__score-row">
                        <span className="rccs-weights__score-num">
                          {num(pts, 2)}
                        </span>
                        <span className="rccs-weights__score-denom">
                          / {c.weight} pts
                        </span>
                      </div>
                      <div className="rccs-weights__track">
                        <div
                          className="rccs-weights__fill"
                          style={{ width: p + "%" }}
                        />
                      </div>
                      <div className="rccs-weights__foot">
                        <span className="rccs-weights__foot-label">
                          Weight: {c.weight} pts max
                        </span>
                        <span className="rccs-weights__foot-pct">
                          {num(p, 0)}%
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {g?.GradeFeedback?.SUMMARY ? (
              <div className="rccs-grade-group">
                <h3>Case Summary</h3>
                <div className="rccs-summary">{g.CaseSummary}</div>
              </div>
            ) : (
              g.CaseSummary && (
                <div className="rccs-grade-group">
                  <h3>Case Summary</h3>
                  <div className="rccs-summary">{g.CaseSummary}</div>
                </div>
              )
            )}

            {order.map(function (cat) {
              // Matches "Preventive Care" / "Preventative" etc.
              const isPreventiveNotGraded =
                preventiveOff && cat.toLowerCase().indexOf("prevent") !== -1;
              return (
                <div className="rccs-grade-group" key={cat}>
                  <h3>{cat}</h3>
                  {isPreventiveNotGraded && (
                    <div className="rccs-grade-group__note">
                      Preventive care is not being graded for this case.
                    </div>
                  )}
                  {groups[cat].map(function (it, i) {
                    const tone = isPreventiveNotGraded ? "blue" : gradeTone(it);
                    return (
                      <div className="rccs-grade-item" key={cat + "-" + i}>
                        <div
                          className={
                            "rccs-grade-item__bar rccs-grade-item__bar--" + tone
                          }
                        />
                        <div className="rccs-grade-item__main">
                          <div className="rccs-grade-item__top">
                            <span
                              className={
                                "rccs-grade-item__event rccs-grade-item__event--" +
                                tone
                              }
                            >
                              {(it.EventName || "").trim()}
                            </span>
                            {it.OrderName && (
                              <span className="rccs-grade-item__order">
                                {it.OrderName}
                              </span>
                            )}
                            {typeof it.Importance === "number" && (
                              <span className="rccs-grade-item__imp">
                                Importance {it.Importance}
                              </span>
                            )}
                          </div>
                          {it.GradeExplanation && (
                            <div className="rccs-grade-item__exp">
                              {it.GradeExplanation}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })}

            {Array.isArray(g.OrderSequenceLog) &&
              g.OrderSequenceLog.length > 0 && (
                <div className="rccs-grade-group">
                  <h3>Order Log</h3>
                  <div className="rccs-seqlog">
                    {g.OrderSequenceLog.map(function (entry, i) {
                      const parsed = splitLogTag(entry);
                      return (
                        <div
                          className={
                            "rccs-seqlog__item" +
                            (parsed.tag ? " rccs-seqlog__item--sub" : "")
                          }
                          key={"seqlog-" + i}
                        >
                          <div className="rccs-seqlog__idx">{i + 1}</div>
                          <div className="rccs-seqlog__text">
                            {parsed.tag && (
                              <span className="rccs-seqlog__tag">
                                {parsed.tag}
                              </span>
                            )}
                            {parsed.text}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

            <div style={{ marginTop: 24, display: "flex", gap: 10 }}>
              <button
                className="rccs-btn rccs-btn--primary"
                onClick={this.props.onBackToCases}
              >
                Back to cases
              </button>
              {this.props.onRetry && (
                <button className="rccs-btn" onClick={this.props.onRetry}>
                  Retry this case
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default RapidGradeScreen;
